import { RiftboundCard } from "modules/cards/model/card";
import { capitalizeFirstLetter } from "utils/presentation";
import { ReplacedSymbolsOracleText } from "modules/common/components/ManaFontSymbols";
import { riftboundTokenAppearance } from "modules/cards/presentation/cardRulesText";

interface RiftboundCardInfoProps {
  card: RiftboundCard;
}

export function RiftboundCardInfo({ card }: RiftboundCardInfoProps) {
  const { features } = card;
  const hasStats = card.type === "Legend" || card.type === "Battlefield";

  return (
    <div className="flex flex-col gap-3">
      {/* Header: Name and cost */}
      <div className="mt-2 md:mt-0">
        <div className="flex items-center justify-between gap-2">
          <h1 className="text-primary text-xl font-semibold">{card.name}</h1>
          {!hasStats && card.costPrinted && (
            <span className="badge badge-outline text-base-content text-sm font-bold">
              {card.costPrinted}
            </span>
          )}
        </div>

        {/* Type line */}
        <p className="text-secondary mt-1 text-sm">{card.type}</p>
      </div>

      {/* Domain badges */}
      {features.domains && features?.domains?.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {features.domains.map((d) => (
            <span
              key={d}
              className="badge badge-sm badge-primary badge-outline"
            >
              {d}
            </span>
          ))}
        </div>
      )}

      {/* Tags */}
      {features.tags.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {features.tags.map((tag) => (
            <span
              key={tag}
              className="badge badge-sm badge-secondary badge-outline"
            >
              {capitalizeFirstLetter(tag)}
            </span>
          ))}
        </div>
      )}

      {/* Oracle text */}
      {card.text && (
        <div className="border-base-content/20 border-t pt-2">
          <ReplacedSymbolsOracleText
            oracleText={card.text}
            className="text-base-content text-sm"
            resolveTokenAppearance={riftboundTokenAppearance}
          />
        </div>
      )}

      {/* Flavor text */}
      {card.flavorText && (
        <ReplacedSymbolsOracleText
          oracleText={card.flavorText}
          className="text-secondary text-sm italic"
        />
      )}

      {/* Stats: Might and Cost Power */}
      {(features.might > 0 || features.costPower > 0) && (
        <div className="border-base-300 flex items-center gap-4 border-t pt-2 text-sm">
          {features.might > 0 && (
            <span className="text-base-content">
              <span className="text-secondary">Might:</span>{" "}
              <span className="font-bold">{features.might}</span>
            </span>
          )}
          {features.costPower > 0 && (
            <span className="text-base-content">
              <span className="text-secondary">Cost Power:</span>{" "}
              <span className="font-bold">{features.costPower}</span>
            </span>
          )}
        </div>
      )}

      {/* Illustrator */}
      {card.artist && (
        <p className="text-secondary text-xs">
          Illus. {card.artist}
          {card.number && <span className="ml-2">#{card.number}</span>}
        </p>
      )}
    </div>
  );
}
